import { Checkbox, Form, Radio, Space, Table } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import React, { useEffect, useMemo, useState } from 'react';

import type { EditEdutainmentFormValues } from '../../types';
import { PAYMENT_OPTIONS, PAYMENT_TABLE_DATASOURCE } from './constants';
import { getPaymentTableColumns } from './helpers';
import styles from './index.module.scss';

const { Item, useWatch, useFormInstance } = Form;

export const PricingSection = () => {
  const form = useFormInstance<EditEdutainmentFormValues>();

  const isPaidProgram = useWatch<
    EditEdutainmentFormValues['isPaidProgram'] | undefined
  >('isPaidProgram');
  const checkedPayments = useWatch<
    EditEdutainmentFormValues['payments'] | undefined
  >('payments');

  const initNonMemberChecked = useMemo(() => {
    const pricing: EditEdutainmentFormValues['pricing'] =
      form.getFieldValue('pricing');

    return Boolean(pricing.nonMember.price || pricing.nonMember.point);
  }, [form]);

  const [nonMemberChecked, setNonMemberChecked] =
    useState(initNonMemberChecked);

  useEffect(() => {
    if (!checkedPayments) {
      return;
    }

    const clearFieldValue = (
      fieldName: [
        'pricing',
        keyof EditEdutainmentFormValues['pricing'],
        keyof EditEdutainmentFormValues['pricing']['member']
      ]
    ) => {
      form.setFieldValue(fieldName, null);
    };

    if (!checkedPayments.includes('creditCard')) {
      clearFieldValue(['pricing', 'member', 'price']);
      clearFieldValue(['pricing', 'nonMember', 'price']);
    }

    if (!checkedPayments.includes('point')) {
      clearFieldValue(['pricing', 'member', 'point']);
      clearFieldValue(['pricing', 'nonMember', 'point']);
    }

    if (checkedPayments.length === 0) {
      clearFieldValue(['pricing', 'member', 'limit']);
      clearFieldValue(['pricing', 'nonMember', 'limit']);
    }

    if (!nonMemberChecked) {
      clearFieldValue(['pricing', 'nonMember', 'price']);
      clearFieldValue(['pricing', 'nonMember', 'point']);
      clearFieldValue(['pricing', 'nonMember', 'limit']);
    }
  }, [checkedPayments, form, nonMemberChecked]);

  const handleNonMemberCheckedChange = (event: CheckboxChangeEvent) => {
    setNonMemberChecked(event.target.checked);
  };

  const columns = getPaymentTableColumns({
    nonMemberChecked,
    handleNonMemberCheckedChange,
    checkedPayments,
  });

  return (
    <Space direction="vertical" className={styles.section}>
      <h4>育樂費用</h4>
      <Space direction="vertical" className={styles.fields}>
        <Item
          label="是否為付費課程"
          name="isPaidProgram"
          rules={[{ required: true, message: '必填欄位' }]}
        >
          <Radio.Group>
            <Radio value={true}>付費課程</Radio>
            <Radio value={false}>非付費課程</Radio>
          </Radio.Group>
        </Item>
        {isPaidProgram && (
          <Space direction="vertical" className={styles['w-full']}>
            <div className={styles.payments}>
              <p>費用</p>
              <Space size="middle" align="baseline">
                <p className={styles.paymentsFieldTitle}>開放付費方式</p>
                <Item
                  name="payments"
                  rules={[{ required: true, message: '請至少選擇一種' }]}
                >
                  <Checkbox.Group options={PAYMENT_OPTIONS} />
                </Item>
              </Space>
            </div>

            <Table
              rowKey="id"
              columns={columns}
              dataSource={PAYMENT_TABLE_DATASOURCE}
              pagination={false}
              className={styles.table}
            />
          </Space>
        )}
      </Space>
    </Space>
  );
};
